import {
  Component, Input, Output, EventEmitter, forwardRef,
  ElementRef, HostListener, ChangeDetectorRef, ViewChild,
  AfterViewInit
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-select',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true
    }
  ],
  template: `
    <div class="select-wrapper" style="position:relative;">
      <div class="form-select" [ngClass]="styleClass" [class.focused]="open" [class.disabled]="disabled" (click)="toggle()">
        <span style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;"
              [style.color]="selectedLabel ? 'inherit' : '#6b7280'">
          {{ selectedLabel || placeholder }}
        </span>
        @if (showClear && value != null) {
          <span style="cursor:pointer;padding:0 4px;color:#6b7280;" (click)="clear($event)">&times;</span>
        }
        <span style="margin-left:4px;color:#6b7280;font-size:0.7rem;">&#9660;</span>
      </div>
      @if (open) {
        <div class="select-dropdown" style="position:absolute;top:100%;left:0;right:0;z-index:1001;background:#fff;border:1px solid var(--color-border);border-radius:6px;margin-top:2px;box-shadow:0 4px 12px rgba(0,0,0,0.1);max-height:250px;display:flex;flex-direction:column;">
          <div style="padding:0.4rem;">
            <input #searchInput class="form-input" placeholder="Suchen..." [(ngModel)]="filterText"
                   (ngModelChange)="onFilterChange()"
                   (click)="$event.stopPropagation()"
                   (keydown)="onSearchKeydown($event)"
                   style="font-size:0.8rem;min-height:32px;">
          </div>
          <div style="overflow-y:auto;flex:1;">
            @for (opt of filteredOptions; track optionValue ? opt[optionValue] : opt; let i = $index) {
              <div class="select-option"
                   style="padding:0.4rem 0.75rem;cursor:pointer;font-size:0.875rem;"
                   [style.background]="i === highlightedIndex ? 'var(--color-bg-hover)' : (isSelected(opt) ? 'var(--color-brand-light)' : '')"
                   (mouseenter)="highlightedIndex = i"
                   (click)="selectOption(opt); $event.stopPropagation()">
                {{ getLabel(opt) }}
              </div>
            }
            @if (filteredOptions.length === 0) {
              <div style="padding:0.5rem 0.75rem;color:#6b7280;font-size:0.8rem;">Keine Ergebnisse</div>
            }
          </div>
        </div>
      }
    </div>
  `
})
export class SelectComponent implements ControlValueAccessor, AfterViewInit {
  @Input() options: any[] = [];
  @Input() optionLabel = 'label';
  @Input() optionValue = '';
  @Input() placeholder = '';
  @Input() showClear = false;
  @Input() disabled = false;
  @Input() styleClass = '';
  @Input() autoOpen = false;

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  open = false;
  filterText = '';
  value: any = null;
  highlightedIndex = 0;

  private onChange: (val: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private elementRef: ElementRef, private cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    if (this.autoOpen && !this.disabled) {
      setTimeout(() => {
        this.open = true;
        this.filterText = '';
        this.highlightedIndex = 0;
        setTimeout(() => this.searchInput?.nativeElement.focus(), 0);
      }, 0);
    }
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    if (this.open && !this.elementRef.nativeElement.contains(event.target)) {
      this.open = false;
    }
  }

  get filteredOptions(): any[] {
    if (!this.filterText) return this.options || [];
    const term = this.filterText.toLowerCase();
    return (this.options || []).filter(opt =>
      this.getLabel(opt).toLowerCase().startsWith(term)
    );
  }

  get selectedLabel(): string {
    if (this.value == null) return '';
    const found = (this.options || []).find(opt => this.getValue(opt) === this.value);
    return found ? this.getLabel(found) : '';
  }

  getLabel(opt: any): string {
    if (opt == null) return '';
    return typeof opt === 'object' ? opt[this.optionLabel] ?? '' : String(opt);
  }

  getValue(opt: any): any {
    if (opt == null) return opt;
    return this.optionValue && typeof opt === 'object' ? opt[this.optionValue] : opt;
  }

  isSelected(opt: any): boolean {
    return this.getValue(opt) === this.value;
  }

  toggle(): void {
    if (this.disabled) return;
    this.open = !this.open;
    if (this.open) {
      this.filterText = '';
      this.highlightedIndex = 0;
      setTimeout(() => this.searchInput?.nativeElement.focus(), 0);
    }
  }

  selectOption(opt: any): void {
    this.value = this.getValue(opt);
    this.onChange(this.value);
    this.onTouched();
    this.open = false;
  }

  clear(event: MouseEvent): void {
    event.stopPropagation();
    this.value = null;
    this.onChange(this.value);
    this.onTouched();
  }

  onFilterChange(): void {
    this.highlightedIndex = 0;
  }

  onSearchKeydown(event: KeyboardEvent): void {
    const opts = this.filteredOptions;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (opts.length) this.highlightedIndex = (this.highlightedIndex + 1) % opts.length;
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (opts.length) this.highlightedIndex = (this.highlightedIndex - 1 + opts.length) % opts.length;
        break;
      case 'Tab':
        if (opts.length) {
          event.preventDefault();
          const dir = event.shiftKey ? -1 : 1;
          this.highlightedIndex = (this.highlightedIndex + dir + opts.length) % opts.length;
        }
        break;
      case 'Enter':
        event.preventDefault();
        if (opts.length) this.selectOption(opts[this.highlightedIndex] ?? opts[0]);
        break;
      case 'Escape':
        event.preventDefault();
        this.open = false;
        break;
    }
  }

  writeValue(val: any): void {
    this.value = val;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
